// The world's colors and light in one place (2026-10-26). Every world material reads these Color objects by
// reference (as uniform values, or as a material's or light's own color), so changing one here, or copying a season's
// or an hour's colors into it at runtime, repaints the world on the next frame with no rebuild. The next step drives
// it: seasons, weather and time of day (sky.ts paints a season's colors in once a match and the hour's light every
// frame). The sky light map (look.ts environment) is baked from these and the Stage remakes it when the sky has moved
// on (Stage.light). The verge's tufts and rocks are vertex colors baked at load; its flowers hide by season in the
// shader (shared.uFlowers). The trees' and grass's instance tints are variation only; their hue comes from here.
import * as THREE from 'three';

const C = (hex: number) => new THREE.Color(hex);

export const PALETTE = {
  /** The sun: where it is (toward it), its color and strength. High, from the left and a little in front. */
  sun: { dir: new THREE.Vector3(-0.55, 1, 0.42).normalize(), color: C(0xfff0d6), intensity: 2.0 },
  /** The sky's fill (hemisphere light): from above and bounced from the ground. */
  fill: { sky: C(0xcfe3ff), ground: C(0x6f7f4a), intensity: 0.95 },
  /** The sky at the horizon (the fog and the background) and overhead. */
  sky: { horizon: C(0xd9ecf7), zenith: C(0x6fb2ea) },
  /** The board: felt grass (two shades), the forest floor, sand (two shades), the worn edge, the river's banks, the
   *  board's sides, pebbles, the felt's sheen, and the soft shade under a soldier's feet. */
  ground: {
    grassA: C(0x5e9a3c), grassB: C(0x7cae48), forest: C(0x3d6a2c), sandA: C(0xdcc28c), sandB: C(0xc9a872),
    edge: C(0x9a8f55), bank: C(0x6e5c40), side: C(0x3f6e30), pebble: C(0xb3aa98), sheen: C(0x3a4a20), contact: C(0x1c2a10),
  },
  /** Grass blades and tufts: at the root, at the tip, and a blade gone to straw. */
  grass: { root: C(0x4f7d2c), tip: C(0xa9c95a), dry: C(0xd2c873) },
  /** The little things on the verges: flower colors, the low leafy mound under them, rocks. */
  scatter: { flowers: [C(0xffffff), C(0xffe066), C(0xff9ec4), C(0xbcd8ff)], mound: C(0x5f9a3e), rock: C(0xa8a59b), bankRock: C(0x9c9a92) },
  /** Leaves, two colors per kind that every leaf mixes between (autumn can put red and gold here). */
  foliage: {
    oak: [C(0x3f7a2a), C(0x8ab846)], birch: [C(0x6f9e34), C(0xb9d266)], pine: [C(0x2f6a45), C(0x6a9a52)], bush: [C(0x467f2c), C(0x93bd4e)],
  },
  /** Bark by kind (bushes wear the oak's). */
  bark: { oak: C(0x5b4330), birch: C(0xe8e2d4), pine: C(0x5a3e2c) },
  /** The buildings' trim: window reveals, doors, the tower's slate roof and its gilded finial. */
  trim: { frame: C(0x4a423a), door: C(0x6b4426), slate: C(0x5a6f8a), gold: C(0xd9a63a) },
  water: { deep: C(0x3a8fd0), shallow: C(0x6cc0ea), foam: C(0xeef8ff) },
  stone: { bridge: C(0xb8ad98), voussoir: C(0xc4baa6), cobble: C(0xb0a690), tower: C(0xc4b9a6), keep: C(0xd2c9b6) },
  /** The table under the board. */
  wood: { a: C(0x9a6a3e), b: C(0x7d5230) },
  /** Lit windows (over 1, so Best's bloom takes them). sky.ts brightens them as the light drops. */
  glow: new THREE.Color(2.4, 1.55, 0.62),
  /** The lanterns' glass (sky.ts: dim by day, lit from dusk), and the settled snow (look.ts weathered). */
  lamp: new THREE.Color(0.35, 0.24, 0.12),
  snow: C(0xeef2f8),
  /** A tree's accent, worn by a share of each kind's trees (sky.ts sets the share by season): spring's blossom,
   *  autumn's deepest reds. */
  accent: { oak: C(0xf6c8da), birch: C(0xf6c8da), pine: C(0x2f6a45), bush: C(0xfaf4ec) },
};
